/**
 * Het aanmeldformulier (SPEC §4.1), voor personen en bedrijven.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiFout, metHerkansing, type Aanmeldinvoer, type Muur } from '../api';
import { Euro, Knop, Kroon, Melding, NIVEAUKLEUR, Veld, useTitel } from '../componenten/basis';
import { Logokiezer } from '../componenten/Logokiezer';
import { Pagina } from '../componenten/Schil';
import { MAXIMUM_INCASSO_CENT, alsEuro, uitInvoer } from '../lib/geld';
import { NIVEAUNAMEN, STANDAARD_DREMPELS, type Niveau } from '../lib/niveau';
import { afkoopTotaal } from '../lib/afkoop';
import { STICHTING } from '../lib/teksten';

export function WordHeld() {
  useTitel('Word held');
  const [muur, zetMuur] = useState<Muur | null>(null);
  const [invoer, zetInvoer] = useState<Aanmeldinvoer>({
    soort: 'persoon',
    naam: '',
    email: '',
    telefoon: '',
    weergavenaam: '',
    regel: '',
    website: '',
    logo: null,
    toon_op_muur: true,
    afkoop: null,
    bedrag_cent: null,
    akkoord: false,
  });
  const [bedragTekst, zetBedragTekst] = useState('');
  const [bezig, zetBezig] = useState(false);
  const [fout, zetFout] = useState<string | null>(null);

  useEffect(() => {
    metHerkansing(() => api.haal<Muur>('/api/muur')).then(zetMuur).catch(() => undefined);
  }, []);

  const wijzig = <K extends keyof Aanmeldinvoer>(k: K, w: Aanmeldinvoer[K]) => zetInvoer((v) => ({ ...v, [k]: w }));
  const bedrijf = invoer.soort === 'bedrijf';
  const tarief = bedrijf ? (muur?.tarieven.jaarbijdrage_bedrijf_cent ?? 25000) : (muur?.tarieven.jaarbijdrage_cent ?? 10000);
  const eigenBedrag = bedragTekst.trim() ? uitInvoer(bedragTekst) : null;
  const bedragFout = !bedragTekst.trim()
    ? null
    : eigenBedrag === null || eigenBedrag < tarief
      ? `Minimaal ${alsEuro(tarief)}`
      : eigenBedrag > MAXIMUM_INCASSO_CENT
        ? `Hooguit ${alsEuro(MAXIMUM_INCASSO_CENT)}. Wil je meer geven? Mail helden@hilfertheem.nl.`
        : null;
  const jaarbedrag = eigenBedrag && eigenBedrag >= tarief ? eigenBedrag : tarief;

  async function verstuur(e: FormEvent) {
    e.preventDefault();
    zetFout(null);
    if (!invoer.akkoord) {
      zetFout('Ga akkoord met de voorwaarden en de machtiging om verder te gaan.');
      return;
    }
    if (bedragFout) {
      zetFout(`Een eigen bedrag is minimaal ${alsEuro(tarief)}.`);
      return;
    }
    zetBezig(true);
    try {
      const uit = await api.stuur<{ checkoutUrl: string }>('/api/aanmelden', { ...invoer, bedrag_cent: eigenBedrag && eigenBedrag > tarief ? eigenBedrag : null });
      window.location.href = uit.checkoutUrl;
    } catch (f) {
      zetFout(f instanceof ApiFout ? f.message : 'Er ging iets mis. Probeer het later opnieuw.');
      zetBezig(false);
    }
  }

  const drempels = muur?.drempels ?? STANDAARD_DREMPELS;
  // Afkopen is de jaren van het niveau betalen, het lopende jaar inbegrepen
  // (SPEC §3); het schaalt met het gekozen jaarbedrag.
  const afkoopTotalen: Record<Niveau, number> = {
    brons: jaarbedrag,
    zilver: afkoopTotaal(drempels, jaarbedrag, 'zilver'),
    goud: afkoopTotaal(drempels, jaarbedrag, 'goud'),
  };
  const totaal = afkoopTotalen[invoer.afkoop ?? 'brons'];
  const zilverNa = drempels.zilverNaJaren - 1;
  const goudNa = drempels.goudNaJaren - 1;

  return (
    <Pagina smal>
      <div className="verschijn py-10 sm:py-16">
        <Kroon className="h-10 w-10 text-goud-400" />
        <h1 className="mt-4 text-4xl sm:text-5xl">Word held</h1>
        <p className="mt-3 text-inkt-zacht">
          Vanaf <Euro cent={tarief} /> per kalenderjaar, elk jaar in januari automatisch geïncasseerd. Je staat vanaf vandaag op de muur. Opzeggen kan altijd, per direct.
        </p>

        {muur?.betalen_mogelijk === false && (
          <div className="mt-6">
            <Melding>Aanmelden kan nog net niet: de betaalkoppeling wordt ingericht. Probeer het over een paar dagen opnieuw, of mail helden@hilfertheem.nl.</Melding>
          </div>
        )}

        <form onSubmit={verstuur} className="mt-10 space-y-8">
          <fieldset className="kaart p-6">
            <legend className="sr-only">Persoon of bedrijf</legend>
            <div className="grid gap-3 sm:grid-cols-2">
              {(['persoon', 'bedrijf'] as const).map((s) => (
                <label key={s} className={`flex cursor-pointer flex-col rounded-xl border p-4 transition ${invoer.soort === s ? 'border-goud-600 bg-goud-zacht' : 'border-lijn'}`}>
                  <input type="radio" name="soort" className="sr-only" checked={invoer.soort === s} onChange={() => wijzig('soort', s)} />
                  <span className="font-display text-lg">{s === 'persoon' ? 'Ik meld me aan als persoon' : 'Ik meld een bedrijf aan'}</span>
                  <span className="mt-1 text-sm text-inkt-zacht">
                    {s === 'persoon' ? <>Vanaf <Euro cent={muur?.tarieven.jaarbijdrage_cent ?? 10000} /> per jaar. Je naam op de muur en in de nieuwsbrief, uitnodiging voor de jaarlijkse heldenborrel.</> : <>Vanaf <Euro cent={muur?.tarieven.jaarbijdrage_bedrijf_cent ?? 25000} /> per jaar. Je logo op de muur en in de nieuwsbrief, met een factuur voor je administratie.</>}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="kaart space-y-5 p-6">
            <legend className="sr-only">Wie ben je</legend>
            {bedrijf && (
              <Veld etiket="Bedrijfsnaam" hulp="Zo staat het bedrijf op de muur.">
                <input className="veld" required maxLength={80} value={invoer.weergavenaam} onChange={(e) => wijzig('weergavenaam', e.target.value)} />
              </Veld>
            )}
            <Veld etiket={bedrijf ? 'Contactpersoon' : 'Naam'}>
              <input className="veld" required autoComplete="name" value={invoer.naam} onChange={(e) => wijzig('naam', e.target.value)} />
            </Veld>
            <Veld etiket="E-mailadres" hulp="Je krijgt een persoonlijke pagina om je gegevens te wijzigen, een niveau af te kopen of op te zeggen. Daarvoor sturen we een inloglink naar dit adres; een wachtwoord is er niet.">
              <input className="veld" type="email" required autoComplete="email" value={invoer.email} onChange={(e) => wijzig('email', e.target.value)} />
            </Veld>
            <Veld etiket="Telefoon (niet verplicht)" hulp="Alleen voor als we je even willen bellen, bijvoorbeeld rond de heldenborrel. De uitnodiging zelf komt per mail.">
              <input className="veld" type="tel" autoComplete="tel" value={invoer.telefoon} onChange={(e) => wijzig('telefoon', e.target.value)} />
            </Veld>
          </fieldset>

          <fieldset className="kaart space-y-5 p-6">
            <legend className="sr-only">Op de muur</legend>
            <label className="flex cursor-pointer items-start gap-3">
              <input type="checkbox" className="mt-1 h-4 w-4 accent-goud-500" checked={invoer.toon_op_muur} onChange={(e) => wijzig('toon_op_muur', e.target.checked)} />
              <span>
                <span className="font-medium">{bedrijf ? 'Zet ons logo en onze naam op de muur' : 'Zet mijn naam op de muur'}</span>
                <span className="block text-sm text-inkt-zacht">Uitgevinkt sta je anoniem op de muur, als “{bedrijf ? 'Een bedrijf' : 'Een held'}”. Je telt dan wel gewoon mee in het aantal helden.</span>
              </span>
            </label>
            {invoer.toon_op_muur && !bedrijf && (
              <Veld etiket="Naam op de muur" hulp="Zoals je het wilt zien: je eigen naam, of “Familie Jansen”.">
                <input className="veld" maxLength={80} placeholder={invoer.naam || 'Je naam'} value={invoer.weergavenaam} onChange={(e) => wijzig('weergavenaam', e.target.value)} />
              </Veld>
            )}
            {invoer.toon_op_muur && (
              <Veld etiket="Eén regel eronder (niet verplicht)" hulp={bedrijf ? 'Bijvoorbeeld “Levert al 20 jaar het kampbrood”.' : 'Bijvoorbeeld “Oud-leiding welpen 1994-2001” of “Buurman van De Spaander”.'}>
                <input className="veld" maxLength={80} value={invoer.regel} onChange={(e) => wijzig('regel', e.target.value)} />
              </Veld>
            )}
            {bedrijf && invoer.toon_op_muur && (
              <>
                <Veld etiket="Website (niet verplicht)">
                  <input className="veld" placeholder="www.voorbeeld.nl" value={invoer.website} onChange={(e) => wijzig('website', e.target.value)} />
                </Veld>
                <Logokiezer logo={invoer.logo ?? null} onChange={(l) => wijzig('logo', l)} />
              </>
            )}
          </fieldset>

          <fieldset className="kaart space-y-5 p-6">
            <legend className="sr-only">Bedrag</legend>
            <p className="font-medium">Jaarbijdrage</p>
            <p className="-mt-3 text-sm text-inkt-zacht">
              Het tarief is <Euro cent={tarief} /> per kalenderjaar. Wil je meer geven? Vul dan hieronder het totale bedrag in dat je per jaar wilt bijdragen, bijvoorbeeld <Euro cent={tarief + 5000} />. Dat hele bedrag wordt dan elk jaar afgeschreven.
            </p>
            <Veld etiket="Totale jaarbijdrage (niet verplicht)" fout={bedragFout}>
              <div className="flex items-center gap-2">
                <span className="text-inkt-zacht">€</span>
                <input className="veld max-w-[10rem]" inputMode="decimal" placeholder={alsEuro(tarief).replace('€ ', '')} value={bedragTekst} aria-invalid={Boolean(bedragFout)} onChange={(e) => zetBedragTekst(e.target.value)} />
              </div>
            </Veld>
          </fieldset>

          <fieldset className="kaart p-6">
            <legend className="sr-only">Niveau</legend>
            <p className="font-medium">Begin je als brons, of koop je meteen een hoger niveau?</p>
            <p className="mt-1 text-sm text-inkt-zacht">
              Iedereen begint als brons; na {zilverNa} volle jaren word je vanzelf zilver, na {goudNa} goud. Wil je niet wachten, dan betaal je nu in één keer de jaarbijdragen die dat niveau vraagt en ben je het meteen. Dat is geen vooruitbetaling: vanaf volgend jaar betaal je gewoon weer de jaarbijdrage, net als iedere held. Gouden helden krijgen bovendien een gouden naambordje achter de bar in het clubhuis; gouden bedrijven hun logo.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {(['brons', 'zilver', 'goud'] as Niveau[]).map((n) => {
                const gekozen = (invoer.afkoop ?? 'brons') === n;
                const k = NIVEAUKLEUR[n];
                return (
                  <label key={n} className={`flex cursor-pointer flex-col rounded-xl border p-4 transition ${gekozen ? `${k.rand} ${k.vlak}` : 'border-lijn hover:border-lijn/80'}`}>
                    <input type="radio" name="afkoop" className="sr-only" checked={gekozen} onChange={() => wijzig('afkoop', n === 'brons' ? null : n)} />
                    <span className={`flex items-center gap-2 font-display text-lg ${k.tekst}`}>
                      <Kroon className="h-4 w-4" />
                      {NIVEAUNAMEN[n]}
                    </span>
                    <span className="mt-1 text-sm text-inkt-zacht">
                      {n === 'brons' ? (
                        <>Nu <Euro cent={jaarbedrag} />, daarna elk jaar <Euro cent={jaarbedrag} />.</>
                      ) : (
                        <>Nu <Euro cent={afkoopTotalen[n]} /> ({Math.round(afkoopTotalen[n] / jaarbedrag)} jaarbijdragen in één keer), vanaf volgend jaar <Euro cent={jaarbedrag} /> per jaar.</>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <fieldset className="kaart space-y-4 p-6">
            <legend className="sr-only">Akkoord</legend>
            <label className="flex cursor-pointer items-start gap-3">
              <input type="checkbox" className="mt-1 h-4 w-4 accent-goud-500" checked={invoer.akkoord} onChange={(e) => wijzig('akkoord', e.target.checked)} />
              <span className="text-sm">
                Ik ga akkoord met de <Link to="/voorwaarden">voorwaarden</Link> en machtig de {STICHTING.naam} om elk jaar in januari{' '}
                <Euro cent={jaarbedrag} /> van mijn rekening af te schrijven. De eerste bijdrage betaal ik zo meteen met iDEAL; daarmee geef ik meteen de machtiging af, en de jaren daarna wordt de bijdrage automatisch geïncasseerd van de rekening waarmee ik nu betaal. Opzeggen kan altijd, per direct, op mijn persoonlijke pagina; mijn naam gaat dan van de muur en over het lopende jaar krijg ik geen geld terug.
              </span>
            </label>
            {fout && <Melding soort="fout">{fout}</Melding>}
            <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
              <p className="text-sm text-inkt-zacht">
                Nu te betalen: <strong className="text-inkt"><Euro cent={totaal} /></strong>
                {invoer.afkoop ? <> (jaarbijdrage {NIVEAUNAMEN[invoer.afkoop].toLowerCase()} in één keer)</> : null}
              </p>
              <Knop type="submit" bezig={bezig} disabled={muur?.betalen_mogelijk === false}>
                Naar de betaling
              </Knop>
            </div>
          </fieldset>
        </form>
      </div>
    </Pagina>
  );
}
